import { History, Settings, Trash2, Undo2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Breadcrumb, type BreadcrumbSegment } from '../../../../components/Breadcrumb';
import { Button } from '../../../../components/Button';
import { ErrorState } from '../../../../components/ErrorState';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { Menu } from '../../../../components/Menu';
import { NextActionAlert } from '../../../../components/NextActionAlert';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { PreviewTable } from '../../../../components/PreviewTable';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { Skeleton } from '../../../../components/Skeleton';
import { StatusText } from '../../../../components/table/StatusText';
import { usePermission } from '../../../../auth/AuthContext';
import { ApiError } from '../../../../lib/apiError';
import { formatDateTime } from '../../../../lib/formatDate';
import { personsApi } from '../../../human-resources/personnel/personnelApi';
import { SupplierModal } from '../components/SupplierModal';
import { suppliersApi } from '../suppliersApi';
import type { SupplierDetail } from '../supplierTypes';

/**
 * One supplier — who they are, how to reach them, and who to ask for.
 *
 * On the **header** layout: a supplier reads as one continuous thing rather than a record whose
 * content splits into panels a reader picks between. There are no tabs, because there is one
 * screen of it.
 */
export function SupplierProfilePage() {
  const { id } = useParams<{ id: string }>();
  const canReadPeople = usePermission('personnel.read');

  const [supplier, setSupplier] = useState<SupplierDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [acting, setActing] = useState(false);
  const [editing, setEditing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);

  /*
    THE CONTACT'S NAME, in its OWN call and only where there is one to fetch. It is a second
    request with a different lifetime rather than a field on the record — the identity split, and
    the same shape the real property's custodian and the issuance's three signatories take.

    A failure leaves the name unresolved and the page standing: not knowing who to ask for is a
    smaller loss than a page that will not render.
  */
  const [contactName, setContactName] = useState<string | null>(null);

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      suppliersApi
        .get(id, signal)
        .then((next) => {
          if (signal?.aborted) return;

          setSupplier(next);
          setError(null);

          /*
            `!= null`, never `!==`: the API omits a null property entirely
            (`DefaultIgnoreCondition = WhenWritingNull`), so a supplier with no contact arrives
            as `undefined` and a strict comparison would fire a resolve for `[undefined]` on
            every one of them.
          */
          if (next.contactPersonId == null) {
            setContactName(null);
            return;
          }

          personsApi
            .resolve([next.contactPersonId], signal)
            .then((people) => {
              if (signal?.aborted) return;
              setContactName(people[0]?.displayName ?? null);
            })
            .catch(() => setContactName(null));
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(
            caught instanceof ApiError ? caught : new ApiError('Could not load this supplier.', 0),
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  async function setActive(next: boolean) {
    if (!supplier) return;

    setActing(true);
    setError(null);

    try {
      if (next) await suppliersApi.restore(supplier.id);
      else await suppliersApi.remove(supplier.id);

      load();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setActing(false);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    'Procurement',
    'Purchasing',
    { label: 'Settings', to: '/procurement/purchasing/settings' },
    { label: 'Suppliers', to: '/procurement/purchasing/settings/suppliers' },
    supplier?.name ?? 'Supplier',
  ];

  if (loading && !supplier) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-5 p-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-64 w-full max-w-4xl" />
        </div>
      </div>
    );
  }

  if (error && !supplier) {
    return (
      <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} onRetry={() => load()} />
      </div>
    );
  }

  if (!supplier) return null;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        panel="cards"
        id="supplier-profile"
        header={
          <ProfileHeader
            id="supplier-profile-header"
            title={supplier.name}
            meta={[
              supplier.code,
              `${supplier.orderCount} order${supplier.orderCount === 1 ? '' : 's'}`,
              supplier.locationLabel,
            ].filter((v): v is string => Boolean(v))}
            status={
              supplier.isActive ? (
                <StatusText tone="active">Active</StatusText>
              ) : (
                <StatusText tone="inactive">Deleted</StatusText>
              )
            }
            createdBy={supplier.createdByName}
            createdAt={supplier.createdAt}
            actions={
              <Menu
                id="supplier-profile-cog"
                items={[
                  {
                    key: 'history',
                    label: 'History Logs',
                    icon: <History className="h-4 w-4" aria-hidden="true" />,
                    permission: 'audit-logs.read',
                    onClick: () => setHistoryOpen(true),
                  },
                ]}
                trigger={({ open, toggle }) => (
                  <button
                    type="button"
                    onClick={toggle}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-label="Supplier options"
                    className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                  >
                    <Settings className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              />
            }
          />
        }
        banner={
          <NextActionAlert
            icon={
              supplier.isActive ? (
                <Undo2 className="h-4 w-4" aria-hidden="true" />
              ) : (
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              )
            }
            title={supplier.isActive ? 'Registered' : 'Deleted'}
            description={
              supplier.isActive
                ? 'They are offered on the purchase-order form. Deleting stops that and leaves every order already placed with them alone.'
                : 'They are no longer offered on the order form. Orders already placed with them still name them.'
            }
            action={
              <PermissionGate permission="suppliers.delete">
                <Button
                  id="supplier-toggle-active"
                  variant={supplier.isActive ? 'secondary' : 'primary'}
                  onClick={() => void setActive(!supplier.isActive)}
                  disabled={acting}
                  leading={
                    supplier.isActive ? (
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    ) : (
                      <Undo2 className="h-4 w-4" aria-hidden="true" />
                    )
                  }
                >
                  {supplier.isActive ? 'Delete' : 'Restore'}
                </Button>
              </PermissionGate>
            }
            id="supplier-profile-banner"
          />
        }
      >
        {error ? (
          <div role="alert" className="max-w-4xl rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        <ProfileCard
          id="supplier-profile-details"
          title="Supplier"
          onEdit={() => setEditing(true)}
          editPermission="suppliers.update"
        >
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Name', value: supplier.name },
              { label: 'Code', value: supplier.code, mono: true },
              { label: 'TIN', value: supplier.taxIdentificationNumber, mono: true },
              { label: 'Email', value: supplier.email, mono: true },
              { label: 'Phone', value: supplier.phone, mono: true },
              { label: 'Address', value: supplier.locationLabel },

              {
                /*
                  THE CONTACT IS A ROW ON THIS CARD, not a card of its own — and that is the
                  "one card, one modal, ONE Edit" rule rather than a layout preference. The
                  contact is filled in on THIS form, so a second card would either need a second
                  Edit opening the same modal, or a cog-less card leaning on this one's — and
                  neither is one of the three exemptions a card with no Edit may take.

                  `Hidden` is NOT an em dash. A reader without `personnel.read` is being told the
                  record HAS a contact and this page has not been given the name; an em dash would
                  state that nobody is named, which is a different fact and not true.
                */
                label: 'Contact person',
                value:
                  supplier.contactPersonId == null
                    ? null
                    : canReadPeople
                      ? contactName
                      : 'Hidden',
              },

              { label: 'Purchase orders', value: String(supplier.orderCount), mono: true },
              { label: 'Notes', value: supplier.notes },
            ]}
          />
        </ProfileCard>

        <ProfileCard id="supplier-profile-audit" title="Audit">
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Created by', value: supplier.createdByName },
              { label: 'Created', value: formatDateTime(supplier.createdAt), mono: true },
              { label: 'Updated by', value: supplier.updatedByName },
              {
                label: 'Updated',
                value: supplier.updatedAt ? formatDateTime(supplier.updatedAt) : null,
                mono: true,
              },
            ]}
          />
        </ProfileCard>
      </ProfileHeaderShell>

      {editing ? (
        <SupplierModal
          open
          editing={supplier}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            load();
          }}
        />
      ) : null}

      <HistoryLogsModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        module="suppliers"
        entityId={supplier.id}
      />
    </div>
  );
}
